import JobAssignmentButton from "./JobAssignmentButton";
import "./ExistingJob.scss";
import Modal from "../Modal/Modal";
import { useEffect, useState, useCallback, useRef } from "react";
import JobCard from "./JobCard";
import JobDetails from "./JobDetails";
import TransporterDetails from "../TransporterDetails";
import JobService from "../../services/JobService";
import { JobOrder, JobStatusEnum } from "../../interfaces/job.interface";
import { useJobContext } from "../../contexts/JobContext";
import JobBreakupTable from "./JobBreakupTable";
import { useLoading } from "../../contexts/LoadingContext";
import ContainerDetails from "./ContainerDetails";
import JobStatus from "./JobStatus";
import noJobInfoGraphics from "./../../assets/images/no-data-infographics.svg";
import { useLoader } from "../../contexts/LoaderContext";
import { generateExcelSheet } from "../../utils/generateExcelSheet";
import { showToast } from "../Toaster";
import { handleExcelFileUpload } from "../../utils/importContainerDataFromExcel";
import { Container } from "../../interfaces/container.interface";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import SwitchComponent from "../SwitchComponent/SwitchComponent";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ButtonDropdown from "../ButtonDropdown/ButtonDropdown";

import { ReactComponent as EditIcon } from "../../assets/icons/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete-icon.svg";
import { ReactComponent as CancelIcon } from "../../assets/icons/cancel.svg";
import { ReactComponent as JobsIcon } from "../../assets/icons/jobs-icon.svg";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { Button } from "@mui/material";
import SearchService from "../../services/SearchService";
import DriverDetails from "./DriverDetails";
import PendencyReport from "./PendencyReport";

interface ExistingJobProps {
  status?: JobStatusEnum;
}

const ExistingJob: React.FC<ExistingJobProps> = () => {
  const { jobStatus } = useParams();
  const [jobs, setJobs] = useState<JobOrder[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobOrder>();
  const [view, setView] = useState("jobDetails");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isImportContainerModalOpen, setIsImportContainerModalOpen] =
    useState<boolean>(false);
  const { shouldFetchJobs, resetJobFetch } = useJobContext();
  const [hasMore, setHasMore] = useState(true);
  const isLoading = useRef(false);
  const skipCount = useRef(0); // Changed from page to skipCount - tracks number of jobs to skip
  const pageSize = 10; // Constant page size
  const loader = useLoading();
  const { showLoader, hideLoader } = useLoader();
  const refreshInterval = 30 * 1000; // 30 seconds
  const [uploadedData, setUploadedData] = useState<Container[]>([]);
  const [validUploadedData, setValidUploadedData] = useState<Container[]>([]);
  const [uploadedJobCode, setUploadedJobCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchString, setSearchString] = useState("");
  const [openDispatchConfirm, setOpenDispatchConfirm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [newJobStatus, setNewJobStatus] = useState("");
  const [jobStatusChangeTitle, setJobStatusChangeTitle] = useState("");
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(false);

  // Convert URL jobStatus to JobStatusEnum
  const getJobStatusFromUrl = (
    status: string | undefined
  ): JobStatusEnum | undefined => {
    if (!status) return undefined;
    const upperStatus = status.toUpperCase();
    if (upperStatus === "IN-PROGRESS") return JobStatusEnum.STARTED;
    switch (upperStatus) {
      case "INITIATED":
        return JobStatusEnum.INITIATED;
      case "ASSIGNED":
        return JobStatusEnum.ASSIGNED;
      case "STARTED":
        return JobStatusEnum.STARTED;
      case "COMPLETED":
        return JobStatusEnum.COMPLETED;
      case "CANCELLED":
        return JobStatusEnum.CANCELLED;
      case "DELETED":
        return JobStatusEnum.DELETED;
      case "EDITED":
        return JobStatusEnum.EDITED;
      default:
        return undefined;
    }
  };

  const currentStatus = getJobStatusFromUrl(jobStatus);

  const options = [
    ...(selectedJob?.jobStatus === JobStatusEnum.INITIATED ||
    selectedJob?.jobStatus === JobStatusEnum.ASSIGNED
      ? [
          { label: "Edit", value: "edit", icon: <EditIcon /> },
          { label: "Delete", value: "delete", icon: <DeleteIcon /> },
          { label: "Cancel", value: "cancel", icon: <CancelIcon /> },
        ]
      : selectedJob?.jobStatus === JobStatusEnum.CANCELLED
      ? [{ label: "Delete", value: "delete", icon: <DeleteIcon /> }]
      : selectedJob?.jobStatus === JobStatusEnum.STARTED
      ? [{ label: "Edit", value: "edit", icon: <EditIcon /> }]
      : []),
  ];

  const refreshSelectedJob = useCallback(async () => {
    if (!selectedJob) return;
    try {
      const updatedJobRes = await JobService.getJobDetails(
        parseInt(selectedJob.jobId)
      );
      const updatedJob = updatedJobRes.data.data;
      if (!updatedJob) return;
      const isJobUpdated =
        JSON.stringify(selectedJob) !== JSON.stringify(updatedJob);
      if (isJobUpdated) {
        setSelectedJob(updatedJob);
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.jobId === updatedJob.jobId ? updatedJob : job
          )
        );
      }
    } catch (error) {
      console.error("Failed to refresh selected job", error);
    }
  }, [selectedJob]);

  const sanitizeJobs = (jobs: JobOrder[]) => {
    return jobs.map(({ links, ...rest }) => rest);
  };

  const loadJobs = useCallback(async () => {
    setJobsLoaded(false);
    isLoading.current = true;
    if (skipCount.current === 0) showLoader();
    if (skipCount.current > 0) loader.setLoading(true);

    try {
      const res = await JobService.getMyJobs({
        limit: pageSize,
        skip: skipCount.current,
        status: currentStatus,
      });

      if (res.data.data?.content) {
        const newJobs = sanitizeJobs(res.data.data.content);

        if (newJobs.length > 0) {
          setJobs((prevJobs) => [
            ...prevJobs,
            ...newJobs.filter(
              (job: JobOrder) =>
                !prevJobs.some((j) => j.jobCode === job.jobCode)
            ),
          ]);
        }

        // Select first job on initial load
        if (skipCount.current === 0 && newJobs.length > 0) {
          handleSelectJob(newJobs[0]);
        }

        // Increment skip count
        skipCount.current += newJobs.length;

        // Check if there are more jobs to load
        const totalJobs = res.data.data.page?.totalElements || 0;

        if (skipCount.current >= totalJobs || newJobs.length < pageSize) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        setHasMore(false);
      }
      setJobsLoaded(true);
    } catch (error) {
      console.error("Error loading jobs", error);
    } finally {
      isLoading.current = false;
      setTimeout(() => {
        loader.setLoading(false);
        hideLoader();
      }, 1000);
    }
  }, [currentStatus, showLoader, hideLoader, loader]);

  const searchJobs = useCallback(async () => {
    setJobsLoaded(false);
    isLoading.current = true;
    try {
      loader.setLoading(true);
      const res = await SearchService.searchJobsOrContainers({
        limit: pageSize,
        skip: skipCount.current,
        status: currentStatus,
        search: searchQuery,
      });
      if (res.data.data?.content) {
        const newJobs = sanitizeJobs(res.data.data.content);
        if (newJobs.length > 0) {
          setJobs((prevJobs) => [
            ...prevJobs,
            ...newJobs.filter(
              (job: JobOrder) =>
                !prevJobs.some((j) => j.jobCode === job.jobCode)
            ),
          ]);
        }

        // Select first job on initial load
        if (skipCount.current === 0 && newJobs.length > 0) {
          handleSelectJob(newJobs[0]);
        }

        // Increment skip count
        skipCount.current += newJobs.length;

        // Check if there are more jobs to load
        const totalJobs = res.data.data.page?.totalElements || 0;

        if (skipCount.current >= totalJobs || newJobs.length < pageSize) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        setJobs([]);
        setHasMore(false);
      }
      setJobsLoaded(true);
    } catch (error) {
      console.error("Error loading search results", error);
    } finally {
      isLoading.current = false;
      setTimeout(() => {
        loader.setLoading(false);
      }, 1000);
    }
  }, [searchQuery, currentStatus, loader]);

  useEffect(() => {
    // Reset on search query or status change
    setJobs([]);
    setSelectedJob(undefined);
    skipCount.current = 0;
    setHasMore(true);
    if (searchQuery === "") {
      loadJobs();
    } else {
      searchJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentStatus]);

  useEffect(() => {
    if (shouldFetchJobs) {
      setJobs([]);
      skipCount.current = 0;
      setHasMore(true);
      loadJobs();
      resetJobFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchJobs, resetJobFetch]);

  const handleScroll = useCallback(() => {
    const scrollContainer = document.querySelector("#jobs-list");
    if (scrollContainer) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;
      const threshold = 200; // Trigger when within 200px of bottom

      if (scrollBottom <= threshold && hasMore && !isLoading.current) {
        if (searchQuery === "") {
          loadJobs();
        } else {
          searchJobs();
        }
      }
    }
  }, [hasMore, loadJobs, searchJobs, searchQuery]);

  useEffect(() => {
    const scrollContainer = document.querySelector("#jobs-list");
    scrollContainer?.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, jobs.length]);

  const handleSelectJob = (job: JobOrder) => {
    setSelectedJob(job);
  };

  const handleViewChange = (view: string) => {
    setView(view);
  };

  const handleUpdatedJob = (updatedJob: JobOrder) => {
    if (!updatedJob || !updatedJob.jobId) {
      console.error("Invalid updated job:", updatedJob);
      return;
    }
    setJobs((prevJobs) =>
      prevJobs.map((job) => (job.jobId === updatedJob.jobId ? updatedJob : job))
    );
    setSelectedJob(updatedJob);
  };

  const downloadExcelTemplate = async () => {
    const headers = ["Job Code", "Container Number", "Size", "Type"];
    const data = [
      {
        // "Job Code": "#" + selectedJob?.jobCode,
        "Job Code": selectedJob?.jobCode,
      },
    ];
    console.log(selectedJob);
    loader.setLoading(true);
    try {
      await generateExcelSheet(headers, data, {
        // fileName: `${"#" + selectedJob?.jobCode}.xlsx`,
        fileName: `${selectedJob?.jobCode}.xlsx`,
        download: true,
      });
      showToast(`Downloaded ${selectedJob?.jobCode}.xlsx`, "success");
      setTimeout(() => {
        loader.setLoading(false);
      }, 1000);
    } catch (error) {
      showToast(`Download ${selectedJob?.jobCode}.xlsx failed`, "error");
      setTimeout(() => {
        loader.setLoading(false);
      }, 1000);
    }
  };

  const readSelectedExcel = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleExcelFileUpload(
      file,
      ({ jobCode, data }) => {
        setUploadedJobCode(jobCode);
        setUploadedData(data);
      },
      (error) => {
        showToast(error, "error");
      }
    );
  };

  const handleOpenImportContainerDataModal = () => {
    setIsImportContainerModalOpen(true);
  };

  const handleCloseImportContainerDataModal = () => {
    setIsImportContainerModalOpen(false);
    setUploadedData([]);
    setValidUploadedData([]);
    setUploadedJobCode("");
  };

  const customHeaderButtons = (
    <>
      <button
        className="excel_download_button"
        type="button"
        onClick={() => {
          downloadExcelTemplate();
        }}
        disabled={loader.isLoading}
      >
        Download Excel File
      </button>

      <button
        className="excel_import_button"
        type="button"
        onClick={handleOpenImportContainerDataModal}
      >
        Import from Excel File
      </button>
    </>
  );

  const isIntegrateButtonEnabled = () => {
    return (
      uploadedJobCode.trim() !== "" &&
      uploadedJobCode ===
        selectedJob?.jobCode.trim().replace(/#/g, "").toUpperCase()
    );
  };

  const integrateUploadedData = () => {
    loader.setLoading(true);
    setValidUploadedData(uploadedData);
    setTimeout(() => {
      handleCloseImportContainerDataModal();
      loader.setLoading(false);
    });
  };

  const handleDispatchOpen = () => setOpenDispatchConfirm(true);
  const handleDispatchClose = () => setOpenDispatchConfirm(false);
  const handleDispatchConfirm = async () => {
    setOpenDispatchConfirm(false);
    if (selectedJob?.jobId === undefined) return;
    try {
      loader.setLoading(true);
      const jobId = parseInt(selectedJob.jobId);
      const dispatchJobRes = await JobService.dispatchJob(jobId);
      if (dispatchJobRes.data.error) {
        showToast(`Failed to dispatch job !`, "error");
      } else {
        refreshSelectedJob();
        showToast(
          `Job ${selectedJob?.jobCode} has been dispatched !`,
          "success"
        );
      }
      setTimeout(() => {
        loader.setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Failed to dispatch selected job", error);
      showToast(`Failed to dispatch selected job !`, "error");
    }
  };

  const [openJobStatusChangeConfirm, setOpenJobStatusChangeConfirm] =
    useState(false);

  const handleJobStatusChangeClose = () => setOpenJobStatusChangeConfirm(false);

  const handleJobStatusChangeConfirm = async () => {
    setOpenJobStatusChangeConfirm(false);
    if (selectedJob?.jobId === undefined) return;
    try {
      loader.setLoading(true);
      const jobId = parseInt(selectedJob.jobId);
      if (newJobStatus === "delete") {
        const deleteJobRes = await JobService.deleteJob(jobId);
        if (deleteJobRes.data.error) {
          showToast(`Failed to delete job !`, "error");
        } else {
          setJobs((prevJobs) =>
            prevJobs.filter((job) => job.jobId !== selectedJob.jobId)
          );
          setSelectedJob(undefined);
          showToast(
            `Job ${selectedJob?.jobCode} has been deleted !`,
            "success"
          );
        }
      } else if (newJobStatus === "cancel") {
        const cancelJobRes = await JobService.cancelJob(jobId);
        if (cancelJobRes.data.error) {
          showToast(`Failed to cancel job !`, "error");
        } else {
          refreshSelectedJob();
          showToast(
            `Job ${selectedJob?.jobCode} has been cancelled !`,
            "success"
          );
        }
      }
      setTimeout(() => {
        loader.setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Failed to delete selected job", error);
      showToast(`Failed to delete selected job !`, "error");
    }
  };

  const handleJobAction = (action: string) => {
    switch (action) {
      case "edit":
        navigate(
          "/jobs/edit/" + selectedJob?.jobId + "/" + selectedJob?.jobCode
        );
        break;
      case "delete":
        setNewJobStatus("delete");
        setJobStatusChangeTitle("Delete Job Confirmation");
        setOpenJobStatusChangeConfirm(true);
        break;
      case "cancel":
        setNewJobStatus("cancel");
        setJobStatusChangeTitle("Cancel Job Confirmation");
        setOpenJobStatusChangeConfirm(true);
        break;
    }
  };

  const handleJobContainers = () => {
    if (selectedJob?.jobId && selectedJob?.jobCode) {
      // Get the current path segments
      const pathSegments = location.pathname.split("/");
      // Find the job status segment (all, initiated, etc.)
      const jobStatusIndex = pathSegments.indexOf("jobs") + 1;
      const jobStatus = pathSegments[jobStatusIndex];

      // Navigate to containers view using the current job status
      navigate(
        `/jobs/${jobStatus}/${selectedJob.jobId}/${selectedJob.jobCode}/containers`
      );
    }
  };

  const handleJobCodeClick = () => {
    if (!selectedJob) return;

    // Get the current path segments
    const pathSegments = location.pathname.split("/");
    // Find the job status segment (all, initiated, etc.)
    const jobStatusIndex = pathSegments.indexOf("jobs") + 1;
    const jobStatus = pathSegments[jobStatusIndex];

    // Navigate to job details using the current job status
    navigate(`/jobs/${jobStatus}/${selectedJob.jobId}/${selectedJob.jobCode}`);
  };

  const handleRefresh = useCallback(async () => {
    if (!selectedJob) return;
    try {
      loader.setLoading(true);
      await refreshSelectedJob();
    } catch (error) {
      console.error("Failed to refresh", error);
      showToast("Failed to refresh data", "error");
    } finally {
      loader.setLoading(false);
    }
  }, [selectedJob, refreshSelectedJob]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAutoRefreshEnabled && selectedJob) {
      interval = setInterval(() => {
        if (
          selectedJob?.jobStatus !== JobStatusEnum.COMPLETED &&
          selectedJob?.jobStatus !== JobStatusEnum.CANCELLED &&
          selectedJob?.jobStatus !== JobStatusEnum.DELETED
        ) {
          handleRefresh();
        }
      }, refreshInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoRefreshEnabled, selectedJob, handleRefresh]);

  return (
    <div className="flex existing-job-page">
      {jobsLoaded && !isLoading.current && jobs.length === 0 && (
        <div className="center_floater_container">
          <div className="no_data_wrapper pr-[126px]">
            {searchQuery && (
              <div className="msg">
                No Job codes / Container numbers found for the search query "
                {searchQuery}"
              </div>
            )}
            {!searchQuery && (
              <>
                <img
                  className="info_graphics"
                  src={noJobInfoGraphics}
                  alt="No Jobs"
                />
                <div className="msg">
                  There are no jobs available at the moment !
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {jobs.length > 0 && (
        <>
          <div className="p-[20px] jobs-list" id="jobs-list">
            <JobCard
              CardData={jobs}
              selectedJobId={selectedJob?.jobCode}
              onSelect={handleSelectJob}
            />
          </div>
          <div className="bg-card  my-[20px] mr-[20px]  rounded-[10px] selected-job-details card-shadow">
            {!selectedJob && (
              <div className="center_floater_container">
                <div className="no_data_wrapper">
                  <img
                    className="info_graphics"
                    src={noJobInfoGraphics}
                    alt="No Jobs"
                  />
                  <div className="msg">Select a job to view the details !</div>
                </div>
              </div>
            )}
            {selectedJob && (
              <div className="details_panel">
                <div className="flex justify-between items-center p-[20px] pb-1">
                  <div className="left flex items-center gap-[8px]">
                    <div
                      className="text-[20px] text-gray-900 cursor-pointer hover:text-blueText transition-colors duration-200 flex items-center gap-[10px]"
                      onClick={handleJobCodeClick}
                      title="View Job Details"
                    >
                      <JobsIcon />
                      {selectedJob.jobCode.toUpperCase()}
                    </div>
                  </div>

                  <div className="flex gap-[10px] items-center">
                    {/* <div className="flex items-center gap-2 mr-2">
                      <SwitchComponent
                        label="Auto Refresh"
                        checked={isAutoRefreshEnabled}
                        onChange={(e) =>
                          setIsAutoRefreshEnabled(e.target.checked)
                        }
                      />
                    </div>
                    <Button
                      className="refresh-button"
                      variant="outlined"
                      onClick={handleRefresh}
                      disableRipple
                      style={{
                        backgroundColor: "#fff",
                        color: "#19303A",
                        textTransform: "capitalize",
                        minWidth: "31px",
                        minHeight: "40px",
                        fontSize: "16px",
                        borderColor: "#19303A",
                        borderRadius: "5px",
                      }}
                    >
                      <RefreshIcon className="w-5 h-5" />
                    </Button> */}
                    <Button
                      className="button-dropdown"
                      variant="outlined"
                      onClick={handleJobContainers}
                      disableRipple
                      style={{
                        backgroundColor: "#fff",
                        color: "#19303A",
                        textTransform: "capitalize",
                        minWidth: "31px",
                        fontSize: "16px",
                        borderColor: "#19303A",
                        borderRadius: "5px",
                      }}
                    >
                      Containers
                    </Button>

                    <JobAssignmentButton job={selectedJob} onAssigned={refreshSelectedJob} />
                    {!selectedJob.availableToDrivers &&
                      selectedJob.jobStatus !== JobStatusEnum.COMPLETED &&
                      selectedJob.jobStatus !== JobStatusEnum.DELETED &&
                      selectedJob.jobStatus !== JobStatusEnum.EDITED &&
                      selectedJob.jobStatus !== JobStatusEnum.CANCELLED && (
                        <Button
                          className="dispatch_button"
                          variant="outlined"
                          onClick={handleDispatchOpen}
                          disableRipple // Removes the ripple effect
                          style={{
                            backgroundColor: "#01ba65",
                            color: "#fff",
                            textTransform: "capitalize",
                            minWidth: "31px",
                            fontSize: "16px",
                            borderColor: "#01ba65",
                            borderRadius: "5px",
                          }}
                        >
                          Dispatch
                        </Button>
                      )}

                    <ButtonDropdown
                      label="Actions"
                      options={options}
                      onSelect={(e: string) => handleJobAction(e)}
                      disabled={
                        selectedJob.jobStatus === JobStatusEnum.COMPLETED ||
                        selectedJob.jobStatus === JobStatusEnum.DELETED ||
                        selectedJob.jobStatus === JobStatusEnum.EDITED
                      }
                    />
                  </div>
                </div>
                <div className="tabs-section">
                  <div className="tabs-header flex flex-row justify-between items-center">
                    <div className="left flex flex-row">
                      <button
                        className={`tab-button ${
                          view === "jobDetails" ? "active" : ""
                        }`}
                        onClick={() => handleViewChange("jobDetails")}
                      >
                        Job Details
                      </button>
                      <button
                        className={`tab-button ${
                          view === "transporterDetails" ? "active" : ""
                        }`}
                        onClick={() => handleViewChange("transporterDetails")}
                      >
                        Transporters
                      </button>
                      {/* <button
                        className={`tab-button ${
                          view === "driverDetails" ? "active" : ""
                        }`}
                        onClick={() => handleViewChange("driverDetails")}
                      >
                        Drivers
                      </button>
                      <button
                        className={`tab-button ${
                          view === "pendencyReport" ? "active" : ""
                        }`}
                        onClick={() => handleViewChange("pendencyReport")}
                      >
                        Pendency Report
                      </button> */}
                    </div>

                    {/* <div className="right">
                      <JobStatus jobStatus={selectedJob.jobStatus} />
                    </div> */}
                  </div>
                </div>

                {view === "jobDetails" && <JobDetails job={selectedJob} />}

                <div
                  className={
                    view === "transporterDetails" ? "visible" : "hidden"
                  }
                >
                  <TransporterDetails jobOrder={selectedJob} />
                </div>

                {/* <div
                  className={view === "driverDetails" ? "visible" : "hidden"}
                >
                  <DriverDetails
                    key={selectedJob?.jobId}
                    jobOrder={selectedJob}
                  />
                </div>

                <div
                  className={view === "pendencyReport" ? "visible" : "hidden"}
                >
                  <PendencyReport job={selectedJob} />
                </div> */}
              </div>
            )}
          </div>
          <Modal
            className="assign_containers_modal"
            title="Assign Containers"
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            headerButtons={customHeaderButtons}
          >
            {selectedJob && (
              <JobBreakupTable
                job={selectedJob}
                onSave={(updatedJob) => handleUpdatedJob(updatedJob)}
                onCancel={() => setIsModalOpen(false)}
                excelImportedData={validUploadedData}
              ></JobBreakupTable>
            )}
          </Modal>

          <Modal
            isOpen={isImportContainerModalOpen}
            onClose={handleCloseImportContainerDataModal}
            title={`Import via Excel for #${selectedJob?.jobCode}`}
            className="fit_content_modal"
          >
            <div className="import_excel_component">
              <div className="upload_area">
                <input
                  type="file"
                  onChange={readSelectedExcel}
                  accept=".xlsx, .xls, .csv"
                />
              </div>
              {uploadedJobCode && (
                <div className="flex justify-center p-[10px]">
                  <p
                    className={`text-sm font-medium ${
                      uploadedJobCode ===
                      selectedJob?.jobCode
                        .trim()
                        .replace(/#/g, "")
                        .toUpperCase()
                        ? "text-green-600"
                        : "text-red-500"
                    }`}
                  >
                    {uploadedJobCode ===
                    selectedJob?.jobCode.trim().replace(/#/g, "").toUpperCase()
                      ? "✔ "
                      : "✘ "}
                    {selectedJob?.jobCode}
                  </p>
                </div>
              )}
              {uploadedData.length > 0 ? (
                <div className="overflow-auto max-h-80 border rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                        >
                          Sl. No.
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                        >
                          Container Number
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                        >
                          Size
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                        >
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {uploadedData.map((data: any, index: any) => (
                        <tr key={index}>
                          <td className="px-6 py-4 text-sm text-gray-700 text-center">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {data.containerNumber}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {data.containerSize}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {data.containerType}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[11px] text-gray-400 mt-2">
                  Please upload an Excel file (.xlsx) to import container data
                </p>
              )}
              <div className="w-full flex justify-center mt-[20px] gap-[20px]">
                <button
                  type="button"
                  onClick={handleCloseImportContainerDataModal}
                  className="secondary_button w-[128px]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={integrateUploadedData}
                  disabled={!isIntegrateButtonEnabled()}
                  className="primary_button  w-[128px]"
                >
                  Import
                </button>
              </div>
            </div>
          </Modal>

          <ConfirmDialog
            open={openDispatchConfirm}
            onClose={handleDispatchClose}
            onConfirm={handleDispatchConfirm}
            title="Dispatch Job Confirmation"
            message={`Are you sure you want to Dispatch the Job #${selectedJob?.jobCode} ?`}
            confirmText="Yes"
            confirmButtonStyle={{
              backgroundColor: "#01ba65",
              color: "#fff",
            }}
          />

          <ConfirmDialog
            open={openJobStatusChangeConfirm}
            onClose={handleJobStatusChangeClose}
            onConfirm={handleJobStatusChangeConfirm}
            title={jobStatusChangeTitle}
            message={`Are you sure you want to ${newJobStatus} the Job #${selectedJob?.jobCode} ?`}
            confirmText="Yes"
            confirmButtonStyle={{
              backgroundColor: "#ff0000",
              color: "#fff",
            }}
          />
        </>
      )}
    </div>
  );
};

export default ExistingJob;
